<script setup lang="ts">
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@zerohuman/ui'
import { computed, ref } from 'vue'
import { api, type Role, type TaskRow } from '@/api'
import { pickedId } from '@/lib/memberPickers'
import { assignableRoles, runsAsRole } from '@/lib/taskRoles'

const props = defineProps<{ task: TaskRow; roles: Role[]; canEdit: boolean }>()
const emit = defineEmits<{ changed: [] }>()
const choices = computed(() => assignableRoles(props.roles))
const selected = computed(() => runsAsRole(props.task, props.roles)?.id)
const busy = ref(false)
const error = ref<string | null>(null)

/** Assignment belongs to this enterprise even when the catalog owns the task's definition. */
async function assign(value: unknown): Promise<void> {
    const roleId = pickedId(value)
    if (!props.canEdit || props.task.origin === 'os' || busy.value || roleId === selected.value) return
    if (!choices.value.some(role => role.id === roleId)) return
    const taskId = props.task.id
    busy.value = true
    error.value = null
    try {
        await api.addRoleTask(roleId, taskId)
        if (props.task.id === taskId) emit('changed')
    } catch {
        if (props.task.id === taskId) error.value = 'Could not assign the role. Try again.'
    } finally {
        busy.value = false
    }
}
</script>

<template>
	<div data-testid="task-role-assignment" class="space-y-1">
		<span class="text-muted-foreground text-sm">Role</span>
		<Select v-if="canEdit && task.origin !== 'os'" :model-value="selected" :disabled="busy || !choices.length" @update:model-value="assign">
			<SelectTrigger aria-label="Assign task role"><SelectValue placeholder="Choose a role" /></SelectTrigger>
			<SelectContent><SelectItem v-for="role in choices" :key="role.id" :value="role.id">{{ role.name }}</SelectItem></SelectContent>
		</Select>
		<p v-else>{{ task.assigneeRoleName ?? task.assigneeRole ?? '—' }}</p>
		<p v-if="canEdit && !choices.length" class="text-muted-foreground text-sm">Create a role before assigning the task.</p>
		<p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
	</div>
</template>
